import { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { API } from "../../Services/api-service";
import { getIPInfo } from "../../lib/IpInfo";
import {
  PAYMENT_STATUS_EVENT,
  claimPaymentCheck,
  closePaymentStatus,
  readPaymentStatus,
  showPaymentStatus,
} from "./utils/paymentStatus";
import { HAS_SEEN_UPGRADE_NOW_KEY, PREMIUM_POPUP_TYPE, premiumPopupTypeFor } from "./utils/statsSession";

const COUNTRY_ALIASES = {
  "united states": "usa",
  "united kingdom": "uk",
  "great britain": "uk",
};

/**
 * The Sandbox premium paywall on a homepage: the premium popups (the blocking
 * one when payment is due, the one-time "upgrade now" one), checkout, and the
 * status popup after the payment provider sends the student back with `?refId=`.
 * Shared by the old homepage and the new one (PremiumGate).
 *
 * Nothing opens or is checked while `isActive` is false (a homepage preloaded
 * out of sight). The payment return is checked once per location; its result is
 * shared (utils/paymentStatus), so the homepage on screen shows it even if the
 * student switched homepages while it loaded.
 *
 * @param {Object} params
 * @param {boolean} params.isActive - the homepage is the one on screen
 * @param {(isChecking: boolean) => void} [params.onCheckingPayment] - the old homepage
 *   shows its loader while the payment status loads
 * @returns {{
 *   premiumPopup: { isOpen: boolean, type: number, data: object|null },
 *   setPremiumPopup: Function,
 *   showSubscriptionPopup: (subscription: object) => boolean,
 *   openPaymentRequired: () => void,
 *   countryName: string,
 *   popups: object,
 * }}
 *   popups — props for SandboxPremiumPopups
 */
const useSandboxPremium = ({ isActive, onCheckingPayment }) => {
  const location = useLocation();
  const history = useHistory();
  const [premiumPopup, setPremiumPopup] = useState({ isOpen: false, type: 1, data: null });
  const [paymentStatus, setPaymentStatus] = useState(readPaymentStatus);
  const [countryName, setCountryName] = useState("");
  const [isUpgrading, setIsUpgrading] = useState(false);
  const checkedLocationRef = useRef(null);

  useEffect(() => {
    const sync = () => setPaymentStatus(readPaymentStatus());
    sync();
    window.addEventListener(PAYMENT_STATUS_EVENT, sync);
    return () => window.removeEventListener(PAYMENT_STATUS_EVENT, sync);
  }, []);

  useEffect(() => {
    if (!isActive || checkedLocationRef.current === location) return;
    checkedLocationRef.current = location;
    const refId = new URLSearchParams(location.search).get("refId");
    if (!refId || !claimPaymentCheck(refId)) return;

    onCheckingPayment?.(true);
    API.get(`/subscription/payment-status?paymentId=${refId}`)
      .then((response) => {
        if (response.data.paymentStatus === 1) showPaymentStatus("success");
        else if (response.data.paymentStatus === 0) showPaymentStatus("failure");
      })
      .catch((error) => console.error("Error checking payment status:", error))
      .finally(() => {
        onCheckingPayment?.(false);
        // Through the router, so the other homepage sees the cleaned URL too and
        // doesn't check the same payment again. Only if the student is still on
        // it: the new homepage has no loader, so they may have moved on.
        if (new URLSearchParams(history.location.search).has("refId")) {
          history.replace(history.location.pathname);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, isActive]);

  const showSubscriptionPopup = (subscription) => {
    const type = premiumPopupTypeFor(subscription);
    if (!type) return false;
    setPremiumPopup({ isOpen: true, type });
    return true;
  };

  const openPaymentRequired = () =>
    setPremiumPopup({ isOpen: true, type: PREMIUM_POPUP_TYPE.PAYMENT_REQUIRED });

  const handleUpgradeClick = async () => {
    const ipInfo = await getIPInfo();
    setCountryName(ipInfo.country || "USA");
    let country = ipInfo.country?.toLowerCase();
    if (COUNTRY_ALIASES[country]) country = COUNTRY_ALIASES[country];
    setIsUpgrading(true);
    try {
      const response = await API.post("/subscription", { country: country || "usa" });
      if (response?.data?.link) window.location.href = response.data.link;
      setIsUpgrading(false);
    } catch (err) {
      console.error("Upgrade failed:", err);
      showPaymentStatus("failure");
      setIsUpgrading(false);
    }
  };

  return {
    premiumPopup,
    setPremiumPopup,
    showSubscriptionPopup,
    openPaymentRequired,
    countryName,
    popups: {
      isActive,
      premiumPopup,
      statusType: paymentStatus.type,
      isStatusPopupOpen: paymentStatus.isOpen,
      countryName,
      isUpgrading,
      onCloseUpgrade: () => {
        setPremiumPopup({ isOpen: false });
        localStorage.setItem(HAS_SEEN_UPGRADE_NOW_KEY, true);
      },
      onUpgrade: handleUpgradeClick,
      onCloseStatus: closePaymentStatus,
      onRetryPayment: () => {
        closePaymentStatus();
        setPremiumPopup({ isOpen: true });
      },
      onGoHome: () => {
        closePaymentStatus();
        setPremiumPopup({ isOpen: false });
      },
    },
  };
};

export default useSandboxPremium;
